/**
 * Landing Page Editor — Editor visual de landing pages.
 * Ruta: /online-store/landing-pages/:landingPageId/editor
 *
 * Panel izquierdo: secciones + form builder + SEO + settings
 * Panel derecho: preview en iframe (futuro) o preview inline
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  TextField,
  Select,
  Checkbox,
  Badge,
  Box,
  Spinner,
  Banner,
  Divider,
  Modal,
  Collapsible,
} from '@/lib/polaris';
import {
  PageIcon,
  PlusIcon,
  DeleteIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EditIcon,
  ViewIcon,
  ExternalIcon,
  ContentIcon,
} from '@/lib/polaris-icons';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { ROUTES } from '@/config/routes.config';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { useParams } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────
interface LandingSection {
  section_type: string;
  display_order: number;
  settings: Record<string, unknown>;
}

interface FormField {
  id: string;
  type: 'text' | 'email' | 'tel' | 'textarea' | 'select' | 'number';
  label: string;
  required: boolean;
  placeholder?: string;
  options?: string[]; // para select
}

interface LandingPageData {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  meta_title: string | null;
  meta_description: string | null;
  og_image_url: string | null;
  template_type: string;
  sections_json: LandingSection[];
  settings_json: Record<string, unknown>;
  custom_css: string | null;
  form_enabled: boolean;
  form_fields: FormField[];
  form_submit_label: string;
  form_success_message: string;
  form_redirect_url: string | null;
  workflow_id: string | null;
  default_utm_source: string | null;
  default_utm_medium: string | null;
  default_utm_campaign: string | null;
  status: string;
  total_views: number;
  total_submissions: number;
  conversion_rate: number;
}

// ─── Section type metadata ────────────────────────────────
const SECTION_TYPES: Record<string, { label: string; description: string }> = {
  'hero': { label: 'Hero / Banner', description: 'Sección principal con título, subtítulo e imagen de fondo' },
  'rich-text': { label: 'Texto enriquecido', description: 'Contenido de texto con formato HTML' },
  'image-with-text': { label: 'Imagen + Texto', description: 'Imagen al lado de texto descriptivo' },
  'image-banner': { label: 'Banner de imagen', description: 'Imagen de ancho completo' },
  'multicolumn': { label: 'Multicolumna', description: 'Contenido en 2-4 columnas' },
  'featured-collection': { label: 'Colección destacada', description: 'Grid de productos de una colección' },
  'featured-product': { label: 'Producto destacado', description: 'Un producto con imagen y botón comprar' },
  'testimonials': { label: 'Testimonios', description: 'Reseñas y testimonios de clientes' },
  'video': { label: 'Video', description: 'Video embebido (YouTube, Vimeo)' },
  'countdown': { label: 'Cuenta regresiva', description: 'Temporizador para ofertas limitadas' },
  'newsletter': { label: 'Newsletter', description: 'Campo de suscripción por email' },
  'contact-form': { label: 'Formulario de contacto', description: 'Formulario de captura de datos' },
  'logo-list': { label: 'Logos de clientes', description: 'Grid de logos de marcas/clientes' },
  'collapsible-content': { label: 'FAQ / Acordeón', description: 'Preguntas frecuentes colapsables' },
  'section-divider': { label: 'Separador', description: 'Línea divisoria entre secciones' },
  'custom-liquid': { label: 'HTML personalizado', description: 'Contenido HTML libre' },
};

const SECTION_TYPE_OPTIONS = Object.entries(SECTION_TYPES).map(([value, { label }]) => ({ label, value }));

const FIELD_TYPE_OPTIONS = [
  { label: 'Texto', value: 'text' },
  { label: 'Email', value: 'email' },
  { label: 'Teléfono', value: 'tel' },
  { label: 'Área de texto', value: 'textarea' },
  { label: 'Número', value: 'number' },
  { label: 'Selector', value: 'select' },
];

// ─── Tabs ─────────────────────────────────────────────────
type EditorTab = 'sections' | 'form' | 'seo' | 'settings' | 'analytics';

const EDITOR_TABS: Array<{ id: EditorTab; label: string }> = [
  { id: 'sections', label: 'Secciones' },
  { id: 'form', label: 'Formulario' },
  { id: 'seo', label: 'SEO' },
  { id: 'settings', label: 'Config' },
  { id: 'analytics', label: 'Analytics' },
];

export default function LandingPageEditorPage() {
  const { landingPageId } = useParams<{ landingPageId: string }>();
  const storeNavigate = useStoreNavigate();
  const { tenantId } = useAuth();
  const toast = useAdminToast();

  const [data, setData] = useState<LandingPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeTab, setActiveTab] = useState<EditorTab>('sections');

  // Section editing
  const [editingSectionIdx, setEditingSectionIdx] = useState<number | null>(null);
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [newSectionType, setNewSectionType] = useState('rich-text');

  // Form field editing
  const [editingFieldIdx, setEditingFieldIdx] = useState<number | null>(null);
  const [addFieldOpen, setAddFieldOpen] = useState(false);

  // ─── Load ──────────────────────────────────────────────
  const loadPage = useCallback(async () => {
    if (!tenantId || !landingPageId) return;
    setLoading(true);

    const { data: page, error } = await supabase
      .from('landing_pages')
      .select('*')
      .eq('id', landingPageId)
      .single();

    if (page) setData(page as unknown as LandingPageData);
    if (error) toast.error('Error al cargar', getErrorMessage(error));
    setLoading(false);
  }, [tenantId, landingPageId]);

  useEffect(() => {
    if (tenantId && landingPageId) loadPage();
  }, [tenantId, landingPageId, loadPage]);

  // ─── Save ──────────────────────────────────────────────
  const handleSave = async () => {
    if (!data || !landingPageId) return;
    setSaving(true);

    try {
      const { error } = await supabase.from('landing_pages').update({
        title: data.title,
        slug: data.slug,
        description: data.description,
        meta_title: data.meta_title,
        meta_description: data.meta_description,
        og_image_url: data.og_image_url,
        sections_json: data.sections_json,
        settings_json: data.settings_json,
        custom_css: data.custom_css,
        form_enabled: data.form_enabled,
        form_fields: data.form_fields,
        form_submit_label: data.form_submit_label,
        form_success_message: data.form_success_message,
        form_redirect_url: data.form_redirect_url,
        workflow_id: data.workflow_id,
        default_utm_source: data.default_utm_source,
        default_utm_medium: data.default_utm_medium,
        default_utm_campaign: data.default_utm_campaign,
      }).eq('id', landingPageId);

      if (error) throw error;
      toast.success('Guardado');
      setDirty(false);
    } catch (error: unknown) {
      toast.error('Error al guardar', getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!data || !landingPageId) return;
    const newStatus = data.status === 'published' ? 'draft' : 'published';
    const updates: Record<string, unknown> = { status: newStatus };
    if (newStatus === 'published') updates.published_at = new Date().toISOString();

    try {
      const { error } = await supabase.from('landing_pages').update(updates).eq('id', landingPageId);
      if (error) throw error;
      setData({ ...data, status: newStatus });
      toast.success(newStatus === 'published' ? 'Publicada' : 'Despublicada');
    } catch (error: unknown) {
      toast.error('Error', getErrorMessage(error));
    }
  };

  // ─── Helpers de actualización ──────────────────────────
  const updateField = <K extends keyof LandingPageData>(key: K, value: LandingPageData[K]) => {
    if (!data) return;
    setData({ ...data, [key]: value });
    setDirty(true);
  };

  // ─── Secciones ─────────────────────────────────────────
  const sections = data?.sections_json ?? [];

  const moveSection = (idx: number, dir: -1 | 1) => {
    const newSections = [...sections];
    const target = idx + dir;
    if (target < 0 || target >= newSections.length) return;
    [newSections[idx], newSections[target]] = [newSections[target], newSections[idx]];
    newSections.forEach((s, i) => s.display_order = i);
    updateField('sections_json', newSections);
  };

  const removeSection = (idx: number) => {
    const newSections = sections.filter((_, i) => i !== idx);
    newSections.forEach((s, i) => s.display_order = i);
    updateField('sections_json', newSections);
    if (editingSectionIdx === idx) setEditingSectionIdx(null);
  };

  const addSection = () => {
    const newSection: LandingSection = {
      section_type: newSectionType,
      display_order: sections.length,
      settings: { heading: SECTION_TYPES[newSectionType]?.label ?? 'Nueva sección' },
    };
    updateField('sections_json', [...sections, newSection]);
    setAddSectionOpen(false);
  };

  const updateSectionSetting = (idx: number, key: string, value: unknown) => {
    const newSections = [...sections];
    newSections[idx] = { ...newSections[idx], settings: { ...newSections[idx].settings, [key]: value } };
    updateField('sections_json', newSections);
  };

  // ─── Form fields ───────────────────────────────────────
  const formFields = data?.form_fields ?? [];

  const addFormField = () => {
    const newField: FormField = {
      id: `field_${Date.now().toString(36)}`,
      type: 'text',
      label: 'Nuevo campo',
      required: false,
      placeholder: '',
    };
    updateField('form_fields', [...formFields, newField]);
    setAddFieldOpen(false);
  };

  const removeFormField = (idx: number) => {
    updateField('form_fields', formFields.filter((_, i) => i !== idx));
    if (editingFieldIdx === idx) setEditingFieldIdx(null);
  };

  const updateFormField = (idx: number, key: keyof FormField, value: unknown) => {
    const newFields = [...formFields];
    newFields[idx] = { ...newFields[idx], [key]: value };
    updateField('form_fields', newFields);
  };

  // ─── Render ────────────────────────────────────────────
  if (loading) {
    return <Page title="Cargando..."><Card><Box padding="800"><InlineStack align="center"><Spinner size="large" /></InlineStack></Box></Card></Page>;
  }

  if (!data) {
    return <Page title="No encontrada"><Banner tone="critical"><p>Landing page no encontrada.</p></Banner></Page>;
  }

  return (
    <Page
      title={
        <PageBreadcrumbTitle
          icon={PageIcon}
          title={data.title}
          onBack={() => storeNavigate(ROUTES.ONLINE_STORE_LANDING_PAGES)}
          backAccessibilityLabel="Volver a landing pages"
        />
      }
      primaryAction={{
        content: saving ? 'Guardando...' : dirty ? 'Guardar cambios' : 'Guardado',
        onAction: handleSave,
        loading: saving,
        disabled: !dirty,
      }}
      secondaryActions={[
        {
          content: data.status === 'published' ? 'Despublicar' : 'Publicar',
          onAction: handlePublish,
          icon: data.status === 'published' ? ViewIcon : ExternalIcon,
        },
      ]}
    >
      <BlockStack gap="400">
        {/* Status bar */}
        <InlineStack gap="200" blockAlign="center">
          <Badge tone={data.status === 'published' ? 'success' : 'info'}>
            {data.status === 'published' ? 'Publicada' : 'Borrador'}
          </Badge>
          {data.status === 'published' && (
            <Text as="span" variant="bodySm" tone="subdued">
              URL: /l/{data.slug} · {data.total_views} visitas · {data.total_submissions} envíos · {Number(data.conversion_rate).toFixed(1)}% conversión
            </Text>
          )}
        </InlineStack>

        {/* Tab navigation */}
        <InlineStack gap="200">
          {EDITOR_TABS.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'primary' : 'secondary'}
              size="slim"
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </Button>
          ))}
        </InlineStack>

        {/* ─── Tab: Secciones ──────────────────────────── */}
        {activeTab === 'sections' && (
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">Secciones ({sections.length})</Text>
                <Button icon={PlusIcon} size="slim" onClick={() => setAddSectionOpen(true)}>Agregar</Button>
              </InlineStack>

              {sections.length === 0 ? (
                <Text as="p" tone="subdued">Sin secciones. Agrega una para empezar a diseñar.</Text>
              ) : (
                <BlockStack gap="200">
                  {sections.map((section, idx) => (
                    <Box
                      key={`${section.section_type}-${idx}`}
                      padding="300"
                      background="bg-surface-secondary"
                      borderRadius="200"
                    >
                      <BlockStack gap="200">
                        <InlineStack align="space-between" blockAlign="center">
                          <InlineStack gap="200" blockAlign="center">
                            <Badge>{SECTION_TYPES[section.section_type]?.label ?? section.section_type}</Badge>
                            {section.settings.heading && (
                              <Text as="span" variant="bodySm" tone="subdued">
                                {String(section.settings.heading).slice(0, 40)}
                              </Text>
                            )}
                          </InlineStack>
                          <InlineStack gap="100">
                            <Button icon={ArrowUpIcon} variant="plain" size="slim" disabled={idx === 0} onClick={() => moveSection(idx, -1)} accessibilityLabel="Mover arriba" />
                            <Button icon={ArrowDownIcon} variant="plain" size="slim" disabled={idx === sections.length - 1} onClick={() => moveSection(idx, 1)} accessibilityLabel="Mover abajo" />
                            <Button icon={EditIcon} variant="plain" size="slim" onClick={() => setEditingSectionIdx(editingSectionIdx === idx ? null : idx)} accessibilityLabel="Editar" />
                            <Button icon={DeleteIcon} variant="plain" size="slim" tone="critical" onClick={() => removeSection(idx)} accessibilityLabel="Eliminar" />
                          </InlineStack>
                        </InlineStack>

                        {/* Inline editor for section settings */}
                        <Collapsible open={editingSectionIdx === idx} id={`section-editor-${idx}`}>
                          <Box paddingBlockStart="200">
                            <BlockStack gap="300">
                              <Divider />
                              <TextField
                                label="Título"
                                value={String(section.settings.heading ?? '')}
                                onChange={(v) => updateSectionSetting(idx, 'heading', v)}
                                autoComplete="off"
                              />
                              {(section.section_type === 'hero' || section.section_type === 'image-banner') && (
                                <TextField
                                  label="Subtítulo"
                                  value={String(section.settings.subheading ?? '')}
                                  onChange={(v) => updateSectionSetting(idx, 'subheading', v)}
                                  autoComplete="off"
                                />
                              )}
                              {section.section_type === 'hero' && (
                                <>
                                  <TextField
                                    label="Texto del botón"
                                    value={String(section.settings.button_label ?? '')}
                                    onChange={(v) => updateSectionSetting(idx, 'button_label', v)}
                                    autoComplete="off"
                                  />
                                  <TextField
                                    label="URL del botón"
                                    value={String(section.settings.button_url ?? '')}
                                    onChange={(v) => updateSectionSetting(idx, 'button_url', v)}
                                    autoComplete="off"
                                  />
                                  <TextField
                                    label="URL imagen de fondo"
                                    value={String(section.settings.background_image_url ?? '')}
                                    onChange={(v) => updateSectionSetting(idx, 'background_image_url', v)}
                                    autoComplete="off"
                                  />
                                </>
                              )}
                              {section.section_type === 'rich-text' && (
                                <TextField
                                  label="Contenido HTML"
                                  value={String(section.settings.content ?? '')}
                                  onChange={(v) => updateSectionSetting(idx, 'content', v)}
                                  multiline={4}
                                  autoComplete="off"
                                />
                              )}
                              {section.section_type === 'video' && (
                                <TextField
                                  label="URL del video (YouTube/Vimeo)"
                                  value={String(section.settings.video_url ?? '')}
                                  onChange={(v) => updateSectionSetting(idx, 'video_url', v)}
                                  autoComplete="off"
                                />
                              )}
                              {section.section_type === 'image-with-text' && (
                                <>
                                  <TextField
                                    label="URL de imagen"
                                    value={String(section.settings.image_url ?? '')}
                                    onChange={(v) => updateSectionSetting(idx, 'image_url', v)}
                                    autoComplete="off"
                                  />
                                  <TextField
                                    label="Contenido"
                                    value={String(section.settings.content ?? '')}
                                    onChange={(v) => updateSectionSetting(idx, 'content', v)}
                                    multiline={3}
                                    autoComplete="off"
                                  />
                                  <Select
                                    label="Layout"
                                    options={[
                                      { label: 'Imagen primero', value: 'image_first' },
                                      { label: 'Texto primero', value: 'text_first' },
                                    ]}
                                    value={String(section.settings.layout ?? 'image_first')}
                                    onChange={(v) => updateSectionSetting(idx, 'layout', v)}
                                  />
                                </>
                              )}
                              {section.section_type === 'countdown' && (
                                <TextField
                                  label="Fecha límite (ISO)"
                                  value={String(section.settings.end_date ?? '')}
                                  onChange={(v) => updateSectionSetting(idx, 'end_date', v)}
                                  placeholder="2026-12-31T23:59:59"
                                  autoComplete="off"
                                />
                              )}
                              {section.section_type === 'multicolumn' && (
                                <Select
                                  label="Columnas"
                                  options={[
                                    { label: '2 columnas', value: '2' },
                                    { label: '3 columnas', value: '3' },
                                    { label: '4 columnas', value: '4' },
                                  ]}
                                  value={String(section.settings.columns ?? '3')}
                                  onChange={(v) => updateSectionSetting(idx, 'columns', Number(v))}
                                />
                              )}
                              {section.section_type === 'custom-liquid' && (
                                <TextField
                                  label="HTML personalizado"
                                  value={String(section.settings.html ?? '')}
                                  onChange={(v) => updateSectionSetting(idx, 'html', v)}
                                  multiline={6}
                                  autoComplete="off"
                                  monospaced
                                />
                              )}
                            </BlockStack>
                          </Box>
                        </Collapsible>
                      </BlockStack>
                    </Box>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        )}

        {/* ─── Tab: Formulario ─────────────────────────── */}
        {activeTab === 'form' && (
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">Formulario de captura</Text>
                <Checkbox
                  label="Formulario habilitado"
                  checked={data.form_enabled}
                  onChange={(v) => updateField('form_enabled', v)}
                  helpText="Si está desactivado, la landing page no mostrará formulario"
                />
              </BlockStack>
            </Card>

            {data.form_enabled && (
              <>
                <Card>
                  <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="h2" variant="headingMd">Campos ({formFields.length})</Text>
                      <Button icon={PlusIcon} size="slim" onClick={addFormField}>Agregar campo</Button>
                    </InlineStack>

                    {formFields.map((field, idx) => (
                      <Box key={field.id} padding="300" background="bg-surface-secondary" borderRadius="200">
                        <BlockStack gap="200">
                          <InlineStack align="space-between" blockAlign="center">
                            <InlineStack gap="200" blockAlign="center">
                              <Badge>{field.type}</Badge>
                              <Text as="span" fontWeight="medium">{field.label}</Text>
                              {field.required && <Badge tone="warning">Requerido</Badge>}
                            </InlineStack>
                            <InlineStack gap="100">
                              <Button icon={EditIcon} variant="plain" size="slim" onClick={() => setEditingFieldIdx(editingFieldIdx === idx ? null : idx)} accessibilityLabel="Editar" />
                              <Button icon={DeleteIcon} variant="plain" size="slim" tone="critical" onClick={() => removeFormField(idx)} accessibilityLabel="Eliminar" />
                            </InlineStack>
                          </InlineStack>

                          <Collapsible open={editingFieldIdx === idx} id={`field-editor-${idx}`}>
                            <Box paddingBlockStart="200">
                              <BlockStack gap="200">
                                <Divider />
                                <TextField label="Etiqueta" value={field.label} onChange={(v) => updateFormField(idx, 'label', v)} autoComplete="off" />
                                <TextField label="ID del campo" value={field.id} onChange={(v) => updateFormField(idx, 'id', v)} autoComplete="off" helpText="Identificador único (sin espacios)" />
                                <Select label="Tipo" options={FIELD_TYPE_OPTIONS} value={field.type} onChange={(v) => updateFormField(idx, 'type', v)} />
                                <TextField label="Placeholder" value={field.placeholder ?? ''} onChange={(v) => updateFormField(idx, 'placeholder', v)} autoComplete="off" />
                                <Checkbox label="Requerido" checked={field.required} onChange={(v) => updateFormField(idx, 'required', v)} />
                                {field.type === 'select' && (
                                  <TextField
                                    label="Opciones (una por línea)"
                                    value={(field.options ?? []).join('\n')}
                                    onChange={(v) => updateFormField(idx, 'options', v.split('\n').filter(Boolean))}
                                    multiline={3}
                                    autoComplete="off"
                                  />
                                )}
                              </BlockStack>
                            </Box>
                          </Collapsible>
                        </BlockStack>
                      </Box>
                    ))}
                  </BlockStack>
                </Card>

                <Card>
                  <BlockStack gap="300">
                    <Text as="h2" variant="headingMd">Configuración del formulario</Text>
                    <TextField
                      label="Texto del botón"
                      value={data.form_submit_label}
                      onChange={(v) => updateField('form_submit_label', v)}
                      autoComplete="off"
                    />
                    <TextField
                      label="Mensaje de éxito"
                      value={data.form_success_message}
                      onChange={(v) => updateField('form_success_message', v)}
                      multiline={2}
                      autoComplete="off"
                    />
                    <TextField
                      label="URL de redirección (opcional)"
                      value={data.form_redirect_url ?? ''}
                      onChange={(v) => updateField('form_redirect_url', v || null)}
                      placeholder="https://..."
                      autoComplete="off"
                      helpText="Si se llena, redirige al usuario después de enviar"
                    />
                  </BlockStack>
                </Card>
              </>
            )}
          </BlockStack>
        )}

        {/* ─── Tab: SEO ────────────────────────────────── */}
        {activeTab === 'seo' && (
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">SEO y Meta Tags</Text>
              <TextField
                label="Título de la página"
                value={data.title}
                onChange={(v) => updateField('title', v)}
                autoComplete="off"
              />
              <TextField
                label="URL slug"
                value={data.slug}
                onChange={(v) => updateField('slug', v)}
                prefix="/l/"
                autoComplete="off"
              />
              <TextField
                label="Meta título"
                value={data.meta_title ?? ''}
                onChange={(v) => updateField('meta_title', v || null)}
                autoComplete="off"
                helpText="Si está vacío se usa el título de la página"
                maxLength={70}
                showCharacterCount
              />
              <TextField
                label="Meta descripción"
                value={data.meta_description ?? ''}
                onChange={(v) => updateField('meta_description', v || null)}
                multiline={2}
                autoComplete="off"
                maxLength={160}
                showCharacterCount
              />
              <TextField
                label="Imagen OG (Open Graph)"
                value={data.og_image_url ?? ''}
                onChange={(v) => updateField('og_image_url', v || null)}
                placeholder="https://..."
                autoComplete="off"
                helpText="Imagen que se muestra al compartir en redes sociales"
              />
              <TextField
                label="Descripción"
                value={data.description ?? ''}
                onChange={(v) => updateField('description', v || null)}
                multiline={2}
                autoComplete="off"
              />
            </BlockStack>
          </Card>
        )}

        {/* ─── Tab: Settings ───────────────────────────── */}
        {activeTab === 'settings' && (
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">UTM por defecto</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Se usan como fallback si el visitante no trae UTM en la URL.
                </Text>
                <TextField
                  label="UTM Source"
                  value={data.default_utm_source ?? ''}
                  onChange={(v) => updateField('default_utm_source', v || null)}
                  placeholder="Ej: facebook, google, email"
                  autoComplete="off"
                />
                <TextField
                  label="UTM Medium"
                  value={data.default_utm_medium ?? ''}
                  onChange={(v) => updateField('default_utm_medium', v || null)}
                  placeholder="Ej: cpc, social, email"
                  autoComplete="off"
                />
                <TextField
                  label="UTM Campaign"
                  value={data.default_utm_campaign ?? ''}
                  onChange={(v) => updateField('default_utm_campaign', v || null)}
                  placeholder="Ej: black-friday-2026"
                  autoComplete="off"
                />
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">CSS personalizado</Text>
                <TextField
                  label="CSS"
                  labelHidden
                  value={data.custom_css ?? ''}
                  onChange={(v) => updateField('custom_css', v || null)}
                  multiline={6}
                  autoComplete="off"
                  monospaced
                  placeholder=".landing-hero { background: #000; }"
                />
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">Automatización</Text>
                <TextField
                  label="ID del Workflow"
                  value={data.workflow_id ?? ''}
                  onChange={(v) => updateField('workflow_id', v || null)}
                  placeholder="UUID del workflow a ejecutar"
                  autoComplete="off"
                  helpText="Al enviar el formulario se enrolla al contacto en este workflow"
                />
              </BlockStack>
            </Card>
          </BlockStack>
        )}

        {/* ─── Tab: Analytics ──────────────────────────── */}
        {activeTab === 'analytics' && (
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">Métricas</Text>
              <InlineStack gap="800" wrap>
                <BlockStack gap="050">
                  <Text as="p" variant="headingLg">{data.total_views.toLocaleString()}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">Visitas totales</Text>
                </BlockStack>
                <BlockStack gap="050">
                  <Text as="p" variant="headingLg">{data.total_submissions.toLocaleString()}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">Envíos de formulario</Text>
                </BlockStack>
                <BlockStack gap="050">
                  <Text as="p" variant="headingLg">{Number(data.conversion_rate).toFixed(1)}%</Text>
                  <Text as="p" variant="bodySm" tone="subdued">Tasa de conversión</Text>
                </BlockStack>
              </InlineStack>
              <Divider />
              <Text as="p" variant="bodySm" tone="subdued">
                Analytics detallados (por día, por UTM) disponibles próximamente en el módulo de reportes.
              </Text>
            </BlockStack>
          </Card>
        )}
      </BlockStack>

      {/* Modal: Agregar sección */}
      <Modal
        open={addSectionOpen}
        onClose={() => setAddSectionOpen(false)}
        title="Agregar sección"
        primaryAction={{ content: 'Agregar', onAction: addSection }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setAddSectionOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Select
              label="Tipo de sección"
              options={SECTION_TYPE_OPTIONS}
              value={newSectionType}
              onChange={setNewSectionType}
            />
            {SECTION_TYPES[newSectionType] && (
              <Text as="p" variant="bodySm" tone="subdued">
                {SECTION_TYPES[newSectionType].description}
              </Text>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
